import { normalize } from "normalizr";
import { matchPath } from "react-router";
import { all, call, delay, put, select, takeLatest } from "redux-saga/effects";

import { LastVisitCounts, forumHeaderUpdate } from "@/mk2/apps/forum/actions";
import { CategoryEntity, CategorySchema, TopicEntity, TopicSchema } from "@/mk2/apps/forum/schemas";
import {
    forumPostNewInCategoryUrl,
    forumPostNewUrl,
    forumTopicsCategoryUrl,
    forumTopicsUrl,
} from "@/mk2/apps/forum/urls";
import { XHRAction, XHRGetError, handleXHRGetErrorSaga, normalizeError } from "@/mk2/helpers/api";
import { removeEmpty, url } from "@/mk2/helpers/urls";
import { getLogger } from "@/mk2/logger";
import { HistoryAction, pushInPWA } from "@/mk2/services/browserHistory";
import { forum_api_topics_anonymous_url, forum_api_topics_url } from "@/mk/urls/functions";

import {
    FORUM_TOPICS_LOAD,
    FORUM_TOPICS_SEARCH,
    ForumTopicsLoadAction,
    ForumTopicsSearchAction,
    forumTopicsFetch,
    forumTopicsLoad,
    forumTopicsUpdateSearch,
} from "./ForumTopics.actions";

const logger = getLogger("forum/ForumTopics.sagas");

interface ApiResponse {
    body: {
        category: CategoryEntity;
        followingTopics: TopicEntity[];
        topics: TopicEntity[];
        hasNext: number;
        hasPrevious: number;
        lastVisitCounts: LastVisitCounts;
    };
}

function* fetchTopics({
    username,
    categorySlug,
    search,
    after,
    xhr,
    location,
    routeNavigate,
}: ForumTopicsLoadAction & XHRAction & HistoryAction) {
    yield put(forumTopicsFetch.request(username, categorySlug, search, after));

    try {
        const response: ApiResponse = yield call(() =>
            xhr.get(
                username === "anonymous"
                    ? forum_api_topics_anonymous_url(categorySlug)
                    : forum_api_topics_url(categorySlug, username),
                removeEmpty({ search, after }),
            ),
        );
        const normalizedResponse = normalize(response.body, {
            category: CategorySchema,
            followingTopics: [TopicSchema],
            topics: [TopicSchema],
        });
        yield put(
            forumTopicsFetch.success(
                username,
                categorySlug,
                search,
                after,
                response.body.hasNext,
                response.body.hasPrevious,
                normalizedResponse,
            ),
        );
        yield put(forumHeaderUpdate(response.body.lastVisitCounts));
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, location, routeNavigate, logger);
        yield put(
            forumTopicsFetch.failure(username, categorySlug, search, after, normalizeError(error)),
        );
    }
}

function* searchTopics({
    username,
    categorySlug,
    search,
    location,
    routeNavigate,
}: ForumTopicsSearchAction & HistoryAction) {
    yield put(forumTopicsUpdateSearch(search));

    // Debounce
    yield delay(300);

    // Run load
    yield put(forumTopicsLoad(username, categorySlug, search));

    const match = matchPath(location.pathname, { path: forumPostNewUrl, exact: false });

    if (match) {
        // We are creating new post
        // Update URL
        if (search) {
            pushInPWA(routeNavigate, url(forumPostNewInCategoryUrl, { categorySlug }, { search }));
        } else if (categorySlug === "all") {
            pushInPWA(routeNavigate, { pathname: forumPostNewUrl, state: { searchFocus: true } });
        } else {
            pushInPWA(routeNavigate, url(forumPostNewInCategoryUrl, { categorySlug }));
        }
    } else {
        // Update URL
        if (search) {
            pushInPWA(routeNavigate, url(forumTopicsCategoryUrl, { categorySlug }, { search }));
        } else if (categorySlug === "all") {
            pushInPWA(routeNavigate, { pathname: forumTopicsUrl, state: { searchFocus: true } });
        } else {
            pushInPWA(routeNavigate, url(forumTopicsCategoryUrl, { categorySlug }));
        }
    }
}

export default function* root() {
    yield all([
        takeLatest(FORUM_TOPICS_LOAD, fetchTopics),
        takeLatest(FORUM_TOPICS_SEARCH, searchTopics),
    ]);
}
